// Shared form primitives for the calendar's editor sheets (slot definition
// editor and the appointment editor): labelled combobox, HH:mm time picker,
// and date/time string helpers. Extracted from slot-form-sheet.jsx so both
// sheets keep one visual language.

import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { Check, ChevronsUpDown, Clock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { resolveTenantDisplayName } from "@/lib/tenant-display";

// Normalizers from fetchTenants()/fetchDoctors() items to combobox options.
// Shared by the slot and appointment editor sheets.
export function toTenantOption(item) {
  const value = String(item?.id || item?._id || "").trim();
  if (!value) return null;
  const label = resolveTenantDisplayName(item, value);
  return { value, label, raw: item };
}

export function toDoctorOption(item) {
  const value = String(item?.id || item?._id || "").trim();
  if (!value) return null;
  const firstName = String(item?.firstName || "").trim();
  const lastName = String(item?.lastName || "").trim();
  const fullName = `${firstName} ${lastName}`.trim();
  const displayName = String(item?.displayName || "").trim();
  const label = fullName || displayName || value;
  return { value, label, raw: item };
}

export function pad2(n) {
  return String(n).padStart(2, "0");
}

export function dateToYmd(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

export function ymdToDate(ymd) {
  if (typeof ymd !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function combineDateAndTimeInZone(ymd, hhmm, timezone) {
  const trimmedTz = String(timezone || "").trim() || "UTC";
  const dt = DateTime.fromISO(`${ymd}T${hhmm}`, { zone: trimmedTz });
  if (!dt.isValid) return null;
  return dt;
}

export function isValidHhmm(value) {
  return typeof value === "string" && /^\d{2}:\d{2}$/.test(value);
}

export const TIME_OPTIONS_15M = (() => {
  const out = [];
  for (let h = 0; h < 24; h += 1) {
    for (let m = 0; m < 60; m += 15) {
      out.push(`${pad2(h)}:${pad2(m)}`);
    }
  }
  return out;
})();

export function FieldLabel({ children }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{children}</p>
  );
}

/**
 * Round color swatch; a checkmark marks the active choice. Shared by the
 * appointment editor and the calendar-entry sheet so both pickers look alike.
 */
export function ColorSwatch({ hex, selected, onSelect, title, disabled }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      title={title || hex}
      aria-pressed={selected}
      disabled={disabled}
      className={cn(
        "flex size-7 shrink-0 items-center justify-center rounded-full border border-black/15 shadow-sm transition-transform hover:scale-110 disabled:pointer-events-none disabled:opacity-50 dark:border-white/20",
        selected && "ring-2 ring-primary ring-offset-2 ring-offset-background",
      )}
      style={{ backgroundColor: hex }}
    >
      {selected ? (
        <Check className="size-3.5 text-white drop-shadow-[0_1px_1px_rgb(0_0_0/0.6)]" strokeWidth={3} aria-hidden />
      ) : null}
    </button>
  );
}

export function ComboboxField({
  label,
  labelSuffix = null,
  value,
  options,
  onChange,
  placeholder = "Select…",
  loading = false,
  disabled = false,
  required = false,
  error,
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((opt) => opt.value === value);

  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <span>
          {label} {required ? <span className="text-destructive">*</span> : null}
        </span>
        {labelSuffix}
      </label>
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              "h-9 w-full justify-between rounded-md border border-input bg-transparent px-3 text-left text-sm font-normal shadow-sm",
              !selected && "text-muted-foreground",
              error && "border-destructive",
            )}
          >
            <span className="truncate">{selected ? selected.label : placeholder}</span>
            {loading ? (
              <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin opacity-50" />
            ) : (
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <Command>
            <CommandInput placeholder={`Search ${label.toLowerCase()}…`} />
            <CommandList>
              <CommandEmpty>No matches.</CommandEmpty>
              <CommandGroup>
                {options.map((opt) => (
                  <CommandItem
                    key={opt.value}
                    value={`${opt.label} ${opt.value}`}
                    onSelect={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                  >
                    <Check className={cn("mr-2 h-4 w-4", value === opt.value ? "opacity-100" : "opacity-0")} />
                    <span className="truncate">{opt.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

/**
 * Popover-based time picker. Mirrors the `DatePicker` trigger (outline button +
 * leading icon) and uses the same `Popover` + `Command` primitives so the
 * design language stays consistent with the rest of the form. Accepts and emits
 * `HH:mm` strings.
 */
export function TimePicker({ value, onChange, placeholder = "HH:MM", className, error }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || "");

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  const handleSelect = (hhmm) => {
    if (!isValidHhmm(hhmm)) return;
    onChange(hhmm);
    setOpen(false);
  };

  const handleDraftSubmit = () => {
    const candidate = String(draft || "").trim();
    if (isValidHhmm(candidate)) {
      handleSelect(candidate);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "flex h-9 w-full justify-start rounded-md border border-input bg-transparent px-3 text-left text-sm font-normal shadow-sm",
            !value && "text-muted-foreground",
            error && "border-destructive",
            className,
          )}
        >
          <Clock className="mr-2 h-4 w-4 opacity-50" aria-hidden />
          {value || <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-44 p-0" align="start">
        <div className="border-b p-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleDraftSubmit();
              }
            }}
            onBlur={handleDraftSubmit}
            placeholder="HH:MM"
            className="h-8 font-mono text-sm"
            inputMode="numeric"
            pattern="[0-9]{2}:[0-9]{2}"
            maxLength={5}
          />
        </div>
        <ScrollArea viewportClassName="max-h-64 py-1">
          {TIME_OPTIONS_15M.map((option) => {
            const selected = option === value;
            return (
              <button
                key={option}
                type="button"
                onClick={() => handleSelect(option)}
                className={cn(
                  "flex w-full items-center justify-between px-3 py-1.5 text-left text-sm font-mono transition-colors hover:bg-accent hover:text-accent-foreground",
                  selected && "bg-accent text-accent-foreground",
                )}
              >
                <span>{option}</span>
                {selected ? <Check className="size-3.5" aria-hidden /> : null}
              </button>
            );
          })}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
